import axios from "axios";
import { baseurl } from "../Basurl/Baseurl";
import { getToken, getUserId } from "../utils/authStorage";

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

const crmUserId = () => getUserId();

/** GET /chat/list?userId=<CRM_MONGO_ID> */
export const fetchChatList = async (userId = crmUserId()) => {
  const response = await axios.get(`${baseurl}chat/list`, {
    params: { userId },
    headers: authHeaders(),
  });
  const data = unwrap(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.chats)) return data.chats;
  if (Array.isArray(data?.list)) return data.list;
  return [];
};

/** GET /chat/messages/<chatId>?userId=&page=&limit= */
export const fetchChatMessages = async (
  chatId,
  { userId = crmUserId(), page = 1, limit = 50 } = {},
) => {
  const encodedId = encodeURIComponent(chatId);
  const response = await axios.get(`${baseurl}chat/messages/${encodedId}`, {
    params: { userId, page, limit },
    headers: authHeaders(),
  });
  const data = unwrap(response);
  if (Array.isArray(data)) return { messages: data, page, hasMore: data.length >= limit };
  if (Array.isArray(data?.messages)) {
    return {
      messages: data.messages,
      page: data.page || page,
      hasMore: data.hasMore ?? data.messages.length >= limit,
      total: data.total,
    };
  }
  return { messages: [], page, hasMore: false };
};

/** POST /chat/start — CRM → Patient */
export const startChat = async (contactUserId, userId = crmUserId()) => {
  const response = await axios.post(
    `${baseurl}chat/start`,
    { userId, contactUserId: String(contactUserId) },
    { headers: authHeaders() },
  );
  return unwrap(response) || response.data;
};

/** POST /chat/send — Supports text and multipart/form-data attachments */
export const sendChatMessageRest = async ({
  message,
  chatId,
  contactUserId,
  attachment,
  userId = crmUserId(),
}) => {
  if (attachment) {
    const formData = new FormData();
    if (userId) formData.append("userId", userId);
    if (chatId) formData.append("chatId", chatId);
    if (contactUserId != null) formData.append("contactUserId", String(contactUserId));
    if (message) formData.append("message", message);
    formData.append("attachment", attachment);

    const response = await axios.post(`${baseurl}chat/send`, formData, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return unwrap(response) || response.data;
  }

  const body = { userId };
  if (message) body.message = message;
  if (chatId) body.chatId = chatId;
  if (contactUserId != null) body.contactUserId = String(contactUserId);

  const response = await axios.post(`${baseurl}chat/send`, body, {
    headers: authHeaders(),
  });
  return unwrap(response) || response.data;
};

/** POST /chat/group/create */
export const createGroupChat = async ({
  name,
  memberIds,
  userId = crmUserId(),
}) => {
  const response = await axios.post(
    `${baseurl}chat/group/create`,
    {
      userId,
      name,
      memberIds: Array.isArray(memberIds) ? memberIds : [],
    },
    { headers: authHeaders() },
  );
  return unwrap(response) || response.data;
};

/** POST /chat/group/remove-member */
export const removeGroupMember = async ({
  chatId,
  memberId,
  userId = crmUserId(),
}) => {
  const response = await axios.post(
    `${baseurl}chat/group/remove-member`,
    { userId, chatId, memberId: String(memberId) },
    { headers: authHeaders() },
  );
  return unwrap(response) || response.data;
};

/** POST /chat/mark-read */
export const markChatReadRest = async (chatId, userId = crmUserId()) => {
  const response = await axios.post(
    `${baseurl}chat/mark-read`,
    { userId, chatId },
    { headers: authHeaders() },
  );
  return unwrap(response) || response.data;
};

/** POST /chat/group/add-member */
export const addGroupMembers = async ({
  chatId,
  memberIds,
  userId = crmUserId(),
}) => {
  const response = await axios.post(
    `${baseurl}chat/group/add-member`,
    {
      userId: String(userId),
      chatId: String(chatId),
      memberIds: Array.isArray(memberIds)
        ? memberIds.map(String)
        : [String(memberIds)],
    },
    { headers: authHeaders() },
  );
  return unwrap(response) || response.data;
};

/** GET /chat/users?search=&page=&limit= */
export const fetchChatUsers = async ({ search = "", page = 1, limit = 50 } = {}) => {
  try {
    const response = await axios.get(`${baseurl}chat/users`, {
      params: { search: search || "", page, limit },
      headers: authHeaders(),
    });
    const data = unwrap(response);
    const rawList =
      (Array.isArray(data) ? data : null) ||
      data?.users ||
      data?.list ||
      data?.data ||
      data?.details ||
      data?.patients ||
      [];

    const list = Array.isArray(rawList) ? rawList : [];
    if (list.length > 0) {
      return list
        .map((u) => {
          const uid = String(u.userId || u.user_id || u._id || u.id || "");
          return {
            userId: uid,
            name:
              u.name ||
              u.patient_name ||
              u.fullname ||
              u.username ||
              u.enq_userName ||
              `User ${uid}`,
            email: u.email || "",
            phone: u.phone || u.mobile || u.contact_no || "",
            image: u.profileImage || u.profile_image || u.image || null,
            role: u.role || "User",
          };
        })
        .filter((m) => m.userId && m.userId !== "undefined" && m.userId !== "null");
    }
  } catch (err) {
    console.error("GET /chat/users notice:", err);
  }
  return fetchPotentialMembers(typeof search === "string" ? search : "");
};

/** Helper to fetch potential group members (patients / staff) */
export const fetchPotentialMembers = async (search = "") => {
  try {
    const res = await axios.get(`${baseurl}all_patients`, {
      params: { page: 1, limit: 100, search: search || "" },
      headers: authHeaders(),
    });
    const details =
      res.data?.Details ||
      res.data?.details ||
      res.data?.data?.details ||
      res.data?.patients ||
      res.data?.data?.patients ||
      (Array.isArray(res.data?.data) ? res.data.data : []) ||
      (Array.isArray(res.data) ? res.data : []);

    const list = Array.isArray(details) ? details : [];
    return list
      .map((p) => {
        const uid = String(p.user_id || p.userId || p._id || "");
        return {
          userId: uid,
          name: p.patient_name || p.name || `Patient ${uid}`,
          email: p.email || "",
          phone: p.mobile || p.phone || "",
          image: p.profile_image || p.image || null,
          role: "Patient",
        };
      })
      .filter((m) => m.userId && m.userId !== "undefined" && m.userId !== "null");
  } catch (err) {
    console.error("Failed to fetch potential members:", err);
    return [];
  }
};
